import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BookOpen, 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Play, 
  ChevronRight,
  Download,
  Settings,
  Users,
  FileSpreadsheet,
  RefreshCw,
  Eye,
  Edit2,
  Filter,
  Pause,
  Volume2,
  VolumeX,
  Maximize
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Tutorial = () => {
  const [expandedStep, setExpandedStep] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const steps = [
    {
      id: 1,
      title: "Select or Create a Client",
      icon: Users,
      description: "Start by selecting an existing client or creating a new one from the client dropdown in the header.",
      details: [
        "Click on the client dropdown in the top navigation bar",
        "Select an existing client from the list",
        "Or click 'Add New Client' to create a new client",
        "Fill in client details: Name, PAN, GSTIN, Contact Person",
        "All data you import will be associated with the selected client"
      ]
    },
    {
      id: 2,
      title: "Export Data from Tally",
      icon: Download,
      description: "Export TDS ledger data from Tally Prime in two formats for accurate import.",
      details: [
        "Open Tally Prime and go to Gateway of Tally",
        "Navigate to: Display → Account Books → Ledger",
        "Select your TDS ledger (e.g., 'TDS ON PROFESSIONAL FEES 194J')",
        "Press Ctrl+E to export → Select 'XML (Data Interchange)'",
        "Save as '194J-Normal.xml' (this is your TDS Reference file)",
        "Now press F5 for Columnar view",
        "Press Ctrl+E again → Export as XML",
        "Save as '194J.xml' (this is your Main Columnar file)"
      ]
    },
    {
      id: 3,
      title: "Import TDS Transactions",
      icon: Upload,
      description: "Use the 'With TDS Reference' method for accurate TDS amounts.",
      details: [
        "Go to TDS Import page",
        "Select the 'With TDS Reference' tab",
        "Step 1: Upload TDS Reference XML (194J-Normal.xml)",
        "Step 2: Upload Main Columnar XML (194J.xml)",
        "Review the 'Sample Matched Transactions' preview",
        "Verify TDS amounts look correct",
        "Click 'Import Transactions' to save"
      ]
    },
    {
      id: 4,
      title: "Review & Fix Data",
      icon: Eye,
      description: "Check imported transactions and fix any issues.",
      details: [
        "Go to TDS Transactions page",
        "Use filters: Show Mismatches, No PAN, No Party Name",
        "Click on TDS Amount to edit if incorrect",
        "Click on Party Name to update for blank entries",
        "Click on PAN to add missing PAN numbers",
        "Use bulk actions to update multiple transactions at once"
      ]
    },
    {
      id: 5,
      title: "Generate Reports",
      icon: FileSpreadsheet,
      description: "View summaries and export reports for filing.",
      details: [
        "Go to TDS Summary page for party-wise and section-wise summaries",
        "Export to Excel for quarterly TDS return filing",
        "Use Reconciliation page to match with TDS returns",
        "Download professional reports for clients"
      ]
    }
  ];

  const videoPlayer = (
    <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden group">
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        src={`${API_URL}/api/tutorial/video`}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        playsInline
      />
      
      {/* Overlay controls */}
      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <Button
          variant="ghost"
          size="lg"
          onClick={togglePlay}
          className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/40 text-white"
        >
          {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
        </Button>
      </div>
      
      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePlay}
              className="text-white hover:bg-white/20"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleMute}
              className="text-white hover:bg-white/20"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white/20"
          >
            <Maximize className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Play button overlay when not playing */}
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={togglePlay}
        >
          <div className="w-20 h-20 rounded-full bg-blue-600/90 flex items-center justify-center shadow-lg hover:bg-blue-600 transition-colors">
            <Play className="w-10 h-10 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6" data-testid="tutorial-page">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center justify-center gap-3">
          <BookOpen className="w-8 h-8 text-blue-600" />
          How to Use TDS Master
        </h1>
        <p className="text-slate-600 mt-2 text-lg">
          Complete guide to import, manage, and reconcile TDS transactions
        </p>
      </div>

      <Tabs defaultValue="steps" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto">
          <TabsTrigger value="steps" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Step-by-Step Guide
          </TabsTrigger>
          <TabsTrigger value="video" className="flex items-center gap-2">
            <Play className="w-4 h-4" />
            Video Tutorial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="video" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-5 h-5 text-blue-600" />
                Video Tutorial
              </CardTitle>
            </CardHeader>
            <CardContent>
              {videoPlayer}
              <p className="text-sm text-slate-500 mt-3 text-center">
                AI-generated explainer video showing how to use TDS Master
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="steps" className="mt-6 space-y-4">
          {steps.map((step) => {
            const Icon = step.icon;
            const isExpanded = expandedStep === step.id;
            
            return (
              <Card 
                key={step.id} 
                className={`transition-all cursor-pointer ${isExpanded ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`}
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isExpanded ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'}`}>
                      <span className="font-bold text-lg">{step.id}</span>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${isExpanded ? 'text-blue-600' : 'text-slate-400'}`} />
                          <h3 className="font-semibold text-lg text-slate-900">{step.title}</h3>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </div>
                      
                      <p className="text-slate-600 mt-1">{step.description}</p>
                      
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t">
                          <ul className="space-y-2">
                            {step.details.map((detail) => (
                              <li key={detail} className="flex items-start gap-2 text-sm text-slate-700">
                                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Quick Tips */}
      <Card className="bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="w-5 h-5" />
            Important Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-1" />
            <p className="text-sm text-amber-800">
              <strong>Always use "With TDS Reference" import</strong> - This ensures accurate TDS amounts by matching voucher numbers.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-1" />
            <p className="text-sm text-amber-800">
              <strong>Export both XML files from the same TDS ledger</strong> - One in normal view (reference) and one in columnar view (party details).
            </p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-1" />
            <p className="text-sm text-amber-800">
              <strong>Check "Sample Matched Transactions" before importing</strong> - Verify that TDS amounts look correct.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-1" />
            <p className="text-sm text-amber-800">
              <strong>Fix missing PANs</strong> - Transactions without PAN are flagged with 20% TDS rate.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Features Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-600" />
            Key Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                <Upload className="w-4 h-4 text-blue-600" />
                Smart Import
              </h4>
              <p className="text-sm text-slate-600">
                Multi-file upload, voucher number matching, duplicate detection
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-600" />
                Auto Detection
              </h4>
              <p className="text-sm text-slate-600">
                PAN-based entity type, TDS rate calculation, mismatch highlighting
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-amber-600" />
                Easy Corrections
              </h4>
              <p className="text-sm text-slate-600">
                Bulk update PAN, party names, TDS amounts, sections
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                <Filter className="w-4 h-4 text-purple-600" />
                Advanced Filters
              </h4>
              <p className="text-sm text-slate-600">
                Multi-section filter, mismatches, no PAN, no party name
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Tutorial;
